import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { fetchTransactionHistory } from '../../../api';
import { getUserBalanceBuckets } from '../../../api/payment/store.api';
import DialogPayment from '../../../components/dialog-payment';
import { BiCoin } from 'react-icons/bi';
import { EmptyComponent } from '../../../components/empty/empty-component';
import { Pagination, Spin } from 'antd';

interface DepositRecord {
  id: string;
  amount: number;
  vnpResponseCode: string;
  time: string;
  method: string;
  transactionId: string;
  bankCode: string;
}

const DepositHistory: React.FC = () => {
  const [deposits, setDeposits] = useState<DepositRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [balance, setBalance] = useState(0);
  const [topUpCa, setTopUpCa] = useState(0); // Ca tự nạp — chỉ để donate/mua, KHÔNG rút được
  const [incomeCa, setIncomeCa] = useState(0); // Ca thu nhập (nhận donate) — RÚT được
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  useEffect(() => {
    const getTransactionHistory = async () => {
      setLoading(true);
      try {
        const response = await fetchTransactionHistory(currentPage, pageSize);
        setDeposits(response.items || []);
        setTotal(response.totalItems || 0);
      } catch (error) {
        console.error('Error fetching transaction history:', error);
      } finally {
        setLoading(false);
      }
    };
    getUserBalanceBuckets()
      .then((res) => {
        setBalance(res.balance);
        setTopUpCa(res.topUpBalance);
        setIncomeCa(res.incomeBalance);
      })
      .catch(() => {});
    getTransactionHistory();
  }, [currentPage, pageSize]);

  const currencyFormatter = new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
    minimumFractionDigits: 0,
  });

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'success':
        return 'text-green-500';
      case 'pending':
        return 'text-yellow-500';
      case 'failed':
      case 'error':
        return 'text-red-500';
      default:
        return 'text-gray-500';
    }
  };

  const getStatusText = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'success':
        return 'Hoàn thành';
      case 'pending':
        return 'Đang xử lý';
      case 'failed':
      case 'error':
        return 'Thất bại';
      default:
        return status;
    }
  };

  return (
    <div className=" relative flex flex-col gap-3 min-w-0 break-words  dark:bg-slate-850 dark:shadow-dark-xl bg-clip-border">
      <div className="w-full flex flex-col border border-[#DEE0E6] dark:border-slate-700 rounded-lg ng-star-inserted">
        <span className="text-[22px] leading-[27px] text-[#18191E]  font-semibold  p-3">
          Biến động số dư
        </span>
        <div className="flex flex-col gap-2 p-3 py-6 bg-[#F6F7F8] dark:bg-slate-800">
          <div className="flex flex-col gap-1">
            <span className="text-sm font-semibold text-[#62677D] dark:text-slate-300">
              Số Ca hiện tại
            </span>
            <span className="text-[20px] leading-7 text-[#18191E] dark:text-white font-semibold ">
              {Number(balance).toLocaleString('vi-VN')} ca
            </span>
          </div>
          {/* Tách 2 loại Ca (do đã tách Ca donate / Ca tự nạp) */}
          <div className="flex flex-wrap gap-x-8 gap-y-1 mt-1 text-sm">
            <div className="flex flex-col">
              <span className="text-[#62677D] dark:text-slate-400">
                Ca thu nhập <span className="text-green-600 dark:text-green-400">(rút được)</span>
              </span>
              <span className="font-semibold text-green-600 dark:text-green-400">
                {Number(incomeCa).toLocaleString('vi-VN')} ca
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-[#62677D] dark:text-slate-400">
                Ca tự nạp <span className="text-[#8a92a3]">(chỉ donate/mua, không rút)</span>
              </span>
              <span className="font-semibold text-[#18191E] dark:text-slate-200">
                {Number(topUpCa).toLocaleString('vi-VN')} ca
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col border border-[#DEE0E6] dark:border-slate-700 rounded-xl">
        <div className="flex justify-between items-center p-3 ">
          <span className="text-[22px] leading-[27px] text-[#18191E]  font-semibold ">
            Lịch sử nạp số dư
          </span>
          <DialogPayment
            onConfirm={(value) => {
              console.log('🚀 ~ PostDonate ~ value:', value);
            }}
            onCancel={() => {
              console.log('🚀 ~ PostDonate ~ value:');
            }}
          />
        </div>
        {loading ? (
          <div className="min-h-[212px] flex justify-center items-center">
            <Spin />
          </div>
        ) : deposits.length > 0 ? (
          <div className="overflow-x-auto bg-[#F6F7F8] px-3">
            <table className="min-w-full divide-y divide-gray-200 ">
              <thead>
                <tr>
                  <th className="px-3 py-[14px] text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Số tiền
                  </th>
                  <th className="px-3 py-[14px] text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ngân hàng
                  </th>
                  <th className="px-3 py-[14px] text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mã giao dịch
                  </th>
                  <th className="px-3 py-[14px] text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Trạng thái
                  </th>
                  <th className="px-3 py-[14px] text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Thời gian
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200 dark:bg-slate-850">
                {deposits.map((deposit) => (
                  <tr className="bg-[#F6F7F8]" key={deposit.id}>
                    <td className="px-3 py-[14px] whitespace-nowrap text-sm font-semibold text-[#26B35E] dark:text-white">
                      {currencyFormatter.format(deposit.amount)}
                    </td>
                    <td className="px-3 py-[14px] whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                      {deposit.bankCode}
                    </td>
                    <td className="px-3 py-[14px] whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                      {deposit.transactionId}
                    </td>
                    <td className="px-3 py-[14px] whitespace-nowrap text-sm">
                      <span
                        className={`py-1 px-6 font-semibold ${
                          deposit.vnpResponseCode === '00'
                            ? 'text-green-600 bg-green-100 rounded-full px-3 inline-block'
                            : 'text-red-600 bg-red-100 rounded-full px-3 inline-block'
                        }`}
                      >
                        {deposit.vnpResponseCode === '00'
                          ? 'Thành công'
                          : 'Thất bại'}
                      </span>
                    </td>
                    <td className="px-3 py-[14px] whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                      {dayjs(deposit.time).format('DD/MM/YYYY HH:mm:ss')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyComponent customClass="bg-[#F6F7F8]" />
        )}
        <div className="flex w-full p-3 rounded-b-lg bg-white justify-end items-center dark:bg-slate-850">
          <Pagination
            current={currentPage}
            total={total}
            pageSize={pageSize}
            showSizeChanger
            onChange={(page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            }}
            locale={{ items_per_page: '/trang' }}
          />
        </div>
      </div>
    </div>
  );
};

export default DepositHistory;
